import { useMemo, useCallback, useState, useEffect } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { LibraryBrowser } from "@/ui-components/LibraryBrowser";
import { portfolioToLibrarySections } from "@/ui-components/LibraryBrowser/adapters";
import { useWatchlistCreator, type WatchlistCreatorMode } from "../hooks/useWatchlistCreator";
import WatchlistBuilderPane from "./WatchlistBuilderPane";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import type { Portfolio } from "@/types/Portfolio";
import type { TagsWatchlist } from "@/shared/api/pref-storage/tags-watchlists";

// Build tag ID -> name lookup
const tagIdToName: Record<string, string> = {};
menuCategories.forEach((cat) => {
  cat.items.forEach((item) => {
    tagIdToName[String(item.id)] = item.title;
  });
});

interface WatchlistCreatorBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  mode: WatchlistCreatorMode;
  /** Pass an existing portfolio to enable edit mode (ticker/keyword) */
  editingPortfolio?: Portfolio | null;
  /** Pass an existing tag watchlist to enable edit mode (tag) */
  editingTagWatchlist?: TagsWatchlist | null;
}

const MODE_TITLES: Record<WatchlistCreatorMode, string> = {
  ticker: "Create Ticker Watchlist",
  keyword: "Create Keyword Watchlist",
  tag: "Create Tag Watchlist",
};

const EDIT_TITLES: Record<WatchlistCreatorMode, string> = {
  ticker: "Edit Ticker Watchlist",
  keyword: "Edit Keyword Watchlist",
  tag: "Edit Tag Watchlist",
};

export default function WatchlistCreatorBrowser({ isOpen, onClose, mode, editingPortfolio, editingTagWatchlist }: WatchlistCreatorBrowserProps) {
  const creator = useWatchlistCreator({
    mode,
    editingPortfolio,
    editingTagWatchlist,
    onCreated: onClose,
    onDeleted: onClose,
  });

  // Build sections — show only the relevant Newsquawk watchlists for this mode
  const sections = useMemo(() => {
    const allSections = portfolioToLibrarySections(
      creator.portfolios,
      [],
      [],
      { tagIdToName }
    );

    // Extract the relevant group from the unified Newsquawk Library section
    const nqSection = allSections.find((s) => s.id === "nq-library");
    if (!nqSection) return [];

    const groupId = mode === "ticker" ? "newsquawk-tickers"
      : mode === "keyword" ? "newsquawk-keywords"
      : "newsquawk-tags";
    const group = nqSection.groups.find((g) => g.id === groupId);
    if (!group) return [];

    // Present as a flat section with just this group
    return [{
      ...nqSection,
      groups: [group],
    }];
  }, [creator.portfolios, mode]);

  // Map imported watchlist IDs to browser selections format
  const browserSelections = useMemo(() => {
    const sel: Record<string, string[]> = {};

    if (mode === "tag") {
      const tagIds = creator.selectedTags.map((t) => String(t.id));
      if (tagIds.length > 0) sel["nq-library"] = tagIds;
    } else {
      const ids = [...creator.importedWatchlistIds].map(String);
      if (ids.length > 0) sel["nq-library"] = ids;
    }

    return sel;
  }, [mode, creator.importedWatchlistIds, creator.selectedTags]);

  // Handle toggle from LibraryBrowser
  const handleToggle = useCallback((sectionId: string, itemId: string) => {
    if (mode === "tag") {
      // For tags, find the tag item and toggle it
      const tagId = Number(itemId);
      if (!isNaN(tagId)) {
        const tagItem = menuCategories
          .flatMap((c) => c.items)
          .find((item) => item.id === tagId);
        if (tagItem) {
          creator.toggleTag({ title: tagItem.title, id: tagItem.id });
        }
      }
    } else {
      // For ticker/keyword, toggle the watchlist import
      const portfolioId = Number(itemId);
      if (!isNaN(portfolioId)) {
        creator.toggleWatchlistImport(portfolioId);
      }
    }
  }, [mode, creator.toggleTag, creator.toggleWatchlistImport]);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handleClose = useCallback(() => {
    creator.reset();
    onClose();
  }, [creator.reset, onClose]);

  const dialogHeader = (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-700/50 bg-gray-800">
      <h2 className="text-sm font-semibold text-gray-100 flex-1">
        {creator.isEditing ? EDIT_TITLES[mode] : MODE_TITLES[mode]}
      </h2>
      <button
        onClick={handleClose}
        className="p-1 text-gray-400 hover:text-gray-200 transition-colors"
      >
        <XMarkIcon className="size-5" />
      </button>
    </div>
  );

  return (
    <Dialog open={isOpen} onClose={handleClose} className="no-drag relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/90 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200"
      />
      <div className="fixed inset-0 z-10 flex items-stretch md:items-center md:justify-center md:p-4">
        <DialogPanel
          transition
          className="w-full md:mx-4 md:h-[85vh] transform overflow-hidden md:rounded-lg bg-gray-800 shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[closed]:sm:scale-95"
        >
          {isMobile ? (
            // Mobile: builder pane is the primary view, scrollable
            <div className="h-full flex flex-col">
              {dialogHeader}
              <div className="flex-1 overflow-y-auto">
                <WatchlistBuilderPane creator={creator} onCancel={handleClose} />
              </div>
            </div>
          ) : (
            // Desktop/tablet: full library browser with builder pane on right
            <LibraryBrowser
              mode="builder"
              sections={sections}
              selections={browserSelections}
              onSelectionsChange={() => {}}
              onToggleOverride={handleToggle}
              className="h-full !rounded-none !border-0"
              sidebarTitle={mode === "tag" ? "Tags" : "Newsquawk"}
              customRightPane={<WatchlistBuilderPane creator={creator} onCancel={handleClose} />}
              header={dialogHeader}
              defaultActivePath={["nq-library"]}
            />
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
